import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  KnowledgeItem,
  KnowledgeItemTypeEnum,
  EmailThread,
  EmailThreadWithMessages,
  DrawingPage,
  AiExtraction,
  UpdateAiExtraction,
  PaginatedResponse,
} from '@estimator/shared';

export function useKnowledgeItems(projectId: string, type?: KnowledgeItemTypeEnum) {
  return useQuery({
    queryKey: ['knowledge', projectId, type],
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<KnowledgeItem>>(
        `/projects/${projectId}/knowledge`,
        { params: type ? { type } : undefined },
      );
      return data;
    },
    enabled: !!projectId,
    refetchInterval: (query) => {
      const items = query.state.data?.data;
      if (items?.some((item) => item.processing_status === 'processing')) {
        return 3000;
      }
      return false;
    },
  });
}

export function useKnowledgeItem(id: string) {
  return useQuery({
    queryKey: ['knowledge-item', id],
    queryFn: async () => {
      const { data } = await apiClient.get<KnowledgeItem>(`/knowledge/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useEmailThreads(projectId: string) {
  return useQuery({
    queryKey: ['email-threads', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<EmailThread[]>(
        `/projects/${projectId}/knowledge/threads`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useEmailThread(threadId: string) {
  return useQuery({
    queryKey: ['email-thread', threadId],
    queryFn: async () => {
      const { data } = await apiClient.get<EmailThreadWithMessages>(
        `/knowledge/threads/${threadId}`,
      );
      return data;
    },
    enabled: !!threadId,
  });
}

export function useUploadKnowledge(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post(
        `/projects/${projectId}/knowledge/upload`,
        formData,
        { headers: { 'Content-Type': undefined } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', projectId] });
    },
  });
}

export function useCreateLink(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string; url: string }) => {
      const { data } = await apiClient.post(
        `/projects/${projectId}/knowledge/link`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', projectId] });
    },
  });
}

export function useDeleteKnowledge(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/knowledge/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', projectId] });
    },
  });
}

export function useMoveKnowledge(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, folderId }: { id: string; folderId: string | null }) => {
      const { data } = await apiClient.patch<KnowledgeItem>(
        `/knowledge/${id}/folder`,
        { folder_id: folderId },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', projectId] });
      queryClient.invalidateQueries({ queryKey: ['folders', projectId] });
    },
  });
}

export function useKnowledgeItemUrl(id: string) {
  return useQuery({
    queryKey: ['knowledge-url', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ url: string }>(`/knowledge/${id}/signed-url`);
      return data;
    },
    enabled: !!id,
  });
}

export function useKnowledgePages(knowledgeItemId: string) {
  return useQuery({
    queryKey: ['knowledge-pages', knowledgeItemId],
    queryFn: async () => {
      const { data } = await apiClient.get<DrawingPage[]>(
        `/knowledge/${knowledgeItemId}/pages`,
      );
      return data;
    },
    enabled: !!knowledgeItemId,
  });
}

export function useKnowledgePageImage(pageId: string) {
  return useQuery({
    queryKey: ['page-image', pageId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ url: string }>(
        `/knowledge/pages/${pageId}/image`,
      );
      return data;
    },
    enabled: !!pageId,
  });
}

export function useAiExtractions(knowledgeItemId: string) {
  return useQuery({
    queryKey: ['ai-extractions', knowledgeItemId],
    queryFn: async () => {
      const { data } = await apiClient.get<AiExtraction[]>(
        `/knowledge/${knowledgeItemId}/extractions`,
      );
      return data;
    },
    enabled: !!knowledgeItemId,
  });
}

export function useUpdateExtraction(knowledgeItemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateAiExtraction & { id: string }) => {
      const { data } = await apiClient.patch<AiExtraction>(
        `/extractions/${id}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-extractions', knowledgeItemId] });
    },
  });
}

export function usePageTileInfo(pageId: string | null) {
  return useQuery({
    queryKey: ['page-tile-info', pageId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/knowledge/pages/${pageId}/tile-info`);
      return data as {
        pageId: string;
        tileStatus: 'pending' | 'processing' | 'ready' | 'failed';
        tileMaxZoom: number | null;
        tileFormat: string;
        width: number | null;
        height: number | null;
        scalePixelsPerUnit: number | null;
        scaleUnit: string;
        /** The pageId value — used by the client to construct API proxy tile URLs */
        tileBaseUrl: string | null;
      };
    },
    enabled: !!pageId,
    refetchInterval: (query) => {
      const status = query.state.data?.tileStatus;
      if (status === 'pending' || status === 'processing') return 2000;
      return false;
    },
  });
}

export function useUpdatePageScale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pageId, pixelsPerUnit, unit }: { pageId: string; pixelsPerUnit: number; unit: string }) => {
      const { data } = await apiClient.patch(`/knowledge/pages/${pageId}/scale`, { pixelsPerUnit, unit });
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['page-tile-info', variables.pageId] });
    },
  });
}
